/**
 * Photos are resized in the browser, before they are ever uploaded.
 *
 * They are stored in D1 (docs/SPEC.md, "Image storage (no R2)"), so the worker
 * refuses anything over 192 KB — and a phone camera produces four megabytes.
 * A face on the ring is drawn a couple of hundred pixels across, and twice
 * that on a dense screen or zoomed in, so a 512 px square at WebP ~0.8 is the
 * size at which it stops looking like a thumbnail; it lands around 40–120 KB.
 * What arrives at the API is the small one; the original never leaves the
 * device.
 *
 * The pipeline takes its decode and encode steps as an argument so the choices
 * — which format, which quality, when to give up a step — can be tested
 * without a canvas.
 */

/** The square the ring draws faces in. */
export const TARGET_SIZE = 512;
/** Fallback edge when quality alone will not get under the ceiling. */
export const SMALLER_SIZE = 384;
/** Aim below this; the worker's hard limit is 192 KB and the margin is deliberate. */
export const TARGET_BYTES = 176 * 1024;

/**
 * Beyond this the picked file is refused before it is decoded. Nothing a phone
 * produces comes near it; a 200-megapixel raw would take the tab down with it.
 */
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

export const WEBP_QUALITY = 0.82;
export const JPEG_QUALITY = 0.85;
/** Tried in order when the first encode comes out too big. */
export const STEP_DOWN = [0.7, 0.6];

export interface Crop {
  sx: number;
  sy: number;
  size: number;
}

export interface Attempt {
  size: number;
  quality: number;
}

export interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  /** Frees the bitmap or object URL behind `source`. */
  release(): void;
}

export interface ResizeIO {
  decode(file: File): Promise<DecodedImage>;
  render(image: DecodedImage, crop: Crop, size: number, type: string, quality: number): Promise<Blob | null>;
}

export interface ResizedImage {
  file: File;
  type: string;
  /** Edge of the square that was produced. */
  size: number;
  quality: number;
  bytes: number;
}

/** The largest centred square inside the picture: a face is nearly always in it. */
export function squareCrop(width: number, height: number): Crop {
  const size = Math.max(1, Math.min(width, height));
  return { sx: Math.round((width - size) / 2), sy: Math.round((height - size) / 2), size };
}

/**
 * Quality first, then size. Dropping to 384 px is visible on a big screen and
 * losing quality is not, so the edge is only given up once quality has been.
 */
export function attemptsFor(type: string): Attempt[] {
  const first = type === 'image/webp' ? WEBP_QUALITY : JPEG_QUALITY;
  return [
    { size: TARGET_SIZE, quality: first },
    ...STEP_DOWN.map((quality) => ({ size: TARGET_SIZE, quality })),
    { size: SMALLER_SIZE, quality: STEP_DOWN[STEP_DOWN.length - 1]! },
  ];
}

const extensionFor = (type: string): string => (type === 'image/webp' ? 'webp' : 'jpg');

function renamed(original: string, type: string): string {
  const stem = original.replace(/\.[^.]+$/, '') || 'photo';
  return `${stem}.${extensionFor(type)}`;
}

/**
 * Squares off, downscales and re-encodes one picked file.
 *
 * WebP first, because it is a third of the size at the same quality; a browser
 * that cannot encode it hands back something else, and that is the signal to
 * take the JPEG ladder instead.
 */
export async function resizeImage(file: File, io: ResizeIO = browserIO): Promise<ResizedImage> {
  const image = await io.decode(file);
  try {
    const crop = squareCrop(image.width, image.height);

    let type = 'image/webp';
    let ladder = attemptsFor(type);
    let best: { blob: Blob; attempt: Attempt } | null = null;

    const first = await io.render(image, crop, ladder[0]!.size, type, ladder[0]!.quality);
    if (!first) throw new Error('This browser could not process that photo.');

    if (first.type !== type) {
      // No WebP encoder here. Whatever came back is what this browser makes of
      // a canvas, so ask for JPEG explicitly and start the ladder again.
      type = 'image/jpeg';
      ladder = attemptsFor(type);
    } else {
      best = { blob: first, attempt: ladder[0]! };
    }

    for (const attempt of best ? ladder.slice(1) : ladder) {
      if (best && best.blob.size <= TARGET_BYTES) break;
      const blob = await io.render(image, crop, attempt.size, type, attempt.quality);
      if (!blob) continue;
      if (!best || blob.size < best.blob.size) best = { blob, attempt };
    }

    if (!best) throw new Error('This browser could not process that photo.');

    const encoded = best.blob.type || type;
    return {
      file: new File([best.blob], renamed(file.name, encoded), { type: encoded }),
      type: encoded,
      size: best.attempt.size,
      quality: best.attempt.quality,
      bytes: best.blob.size,
    };
  } finally {
    image.release();
  }
}

/** `createImageBitmap` where it exists, an `<img>` element where it does not. */
async function decodeInBrowser(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch {
      /* some browsers refuse some files here; the element path still reads them */
    }
  }

  const url = URL.createObjectURL(file);
  const img = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('That file could not be read as an image.'));
      img.src = url;
    });
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
  return {
    source: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    release: () => URL.revokeObjectURL(url),
  };
}

export const browserIO: ResizeIO = {
  decode: decodeInBrowser,
  render(image, crop, size, type, quality) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser could not process that photo.');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image.source, crop.sx, crop.sy, crop.size, crop.size, 0, 0, size, size);
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
  },
};
